import PropTypes from "prop-types";
import { Empty } from "antd";
import TripRecordImages from "./TripRecordImages";
import { buildTripRecordData, formatFuelLevel, formatOdometer } from "./tripRecordsUtils";
import { formatReservationDate, formatReservationTime } from "../utils";

const RecordSection = ({ label, images, emptyDescription }) => (
    <div>
        <p className="fontRegular mb-2 text-xs uppercase tracking-[0.08em] text-[#9b9ba5]">{label}</p>
        <TripRecordImages images={images} emptyDescription={emptyDescription} />
    </div>
);

RecordSection.propTypes = {
    label: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    emptyDescription: PropTypes.string.isRequired,
};

const MetricRow = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="fontRegular text-sm text-[#7f8c8d]">{label}</span>
        <span className="fontSemi text-sm text-[#2F2F46]">{value}</span>
    </div>
);

MetricRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

const TripRecordCard = ({ title, timestamp, record, t }) => {
    const data = buildTripRecordData(record);
    const formattedTimestamp = timestamp
        ? `${formatReservationDate(timestamp, "MMM D, YYYY")} · ${formatReservationTime(timestamp)}`
        : null;

    if (!data.hasData) {
        return (
            <div className="rounded-2xl border border-[#f0f0f0] bg-[#FAFAFC] p-4 lg:p-5">
                <h3 className="fontSemi text-base text-[#2F2F46]">{title}</h3>
                {formattedTimestamp ? (
                    <p className="fontRegular mt-1 text-sm text-[#7f8c8d]">{formattedTimestamp}</p>
                ) : null}
                <div className="flex min-h-[220px] items-center justify-center">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t("upcomingDetails.tripRecords.noRecordPlaceholder")}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-[#f0f0f0] bg-[#FAFAFC] p-4 lg:p-5">
            <h3 className="fontSemi text-base text-[#2F2F46]">{title}</h3>
            {formattedTimestamp ? (
                <p className="fontRegular mt-1 mb-4 text-sm text-[#7f8c8d]">{formattedTimestamp}</p>
            ) : (
                <div className="mb-4" />
            )}

            <div className="flex flex-col gap-5">
                <RecordSection
                    label={t("upcomingDetails.tripRecords.vehiclePhotographs")}
                    images={data.walkaroundImages}
                    emptyDescription={t("upcomingDetails.tripRecords.noVehiclePhotos")}
                />
                <RecordSection
                    label={t("upcomingDetails.tripRecords.damagePhotographs")}
                    images={data.damageImages}
                    emptyDescription={t("upcomingDetails.tripRecords.noDamagePhotos")}
                />
                <RecordSection
                    label={t("upcomingDetails.tripRecords.dashboard")}
                    images={data.dashboardImages}
                    emptyDescription={t("upcomingDetails.tripRecords.dashboardNotCaptured")}
                />

                {data.fuelLevel || data.mileage != null ? (
                    <div className="space-y-2 border-t border-[#ececf3] pt-4">
                        {data.fuelLevel ? (
                            <MetricRow
                                label={t("upcomingDetails.tripRecords.fuelLevel")}
                                value={formatFuelLevel(data.fuelLevel, t)}
                            />
                        ) : null}
                        {data.mileage != null ? (
                            <MetricRow
                                label={t("upcomingDetails.tripRecords.odometer")}
                                value={formatOdometer(data.mileage)}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

TripRecordCard.propTypes = {
    title: PropTypes.string.isRequired,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    record: PropTypes.object,
    t: PropTypes.func.isRequired,
};

export default TripRecordCard;
